import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { focusMainTransiently } from "@/features/account/focus-main";
import { isCeremonyActive, subscribeCeremonyActivity } from "@/lib/webauthn";
import { syncInertWindow } from "./inert-replay";

const ceremonyInactive = () => false;

const APP_ID = "app";
const SCRIM_ID = "scrim";
const DIALOG_HOST_ID = "dialogs";

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function focusableIn(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (node) => !node.hasAttribute("disabled") && !node.hidden,
  );
}

function firstFocusable(el: HTMLElement): HTMLElement | undefined {
  return focusableIn(el)[0];
}

function markedTarget(el: HTMLElement): HTMLElement | undefined {
  const marked = el.querySelector<HTMLElement>("[data-autofocus]");
  return marked && !marked.hasAttribute("disabled") ? marked : undefined;
}

function initialFocusTarget(el: HTMLElement): HTMLElement | undefined {
  return (
    markedTarget(el) ??
    Array.from(
      el.querySelectorAll<HTMLElement>('input:not([type="hidden"]), textarea, select'),
    ).find((node) => !node.hasAttribute("disabled") && !node.hidden)
  );
}

const KEYBOARDLESS_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function raisesKeyboard(el: HTMLElement): boolean {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !KEYBOARDLESS_INPUT_TYPES.has(el.type);
  return el.isContentEditable;
}

function container(id: string, parent: () => HTMLElement | null): HTMLElement | null {
  const existing = document.getElementById(id);
  if (existing) return existing;
  const host = parent();
  if (!host) return null;
  const created = document.createElement("div");
  created.id = id;
  host.appendChild(created);
  return created;
}

function appElement(): HTMLElement | null {
  return document.getElementById(APP_ID);
}

function scrimHost(): HTMLElement | null {
  return container(SCRIM_ID, () => document.getElementById("root") ?? document.body);
}

function dialogHost(): HTMLElement | null {
  return container(DIALOG_HOST_ID, () => document.body);
}

interface CloseWatcherLike {
  addEventListener: (type: "close", listener: () => void) => void;
  destroy: () => void;
}

function newCloseWatcher(): CloseWatcherLike | null {
  const ctor = (window as unknown as { CloseWatcher?: new () => CloseWatcherLike }).CloseWatcher;
  if (!ctor) return null;
  try {
    return new ctor();
  } catch {
    return null;
  }
}

const openDialogs: string[] = [];
const dialogNodes = new Map<string, HTMLDialogElement>();
let lastControlOutsideDialogs: HTMLElement | null = null;
let lastPointerType: string | null = null;

function isAppControlOutsideDialogs(candidate: Element | null): candidate is HTMLElement {
  return (
    candidate instanceof HTMLElement &&
    candidate.closest(`#${APP_ID}`) !== null &&
    candidate.closest("dialog") === null
  );
}

function trackOutsideDialogs(candidate: Element | null): void {
  if (isAppControlOutsideDialogs(candidate)) lastControlOutsideDialogs = candidate;
}

function trackFocusIn(event: FocusEvent): void {
  trackOutsideDialogs(event.target instanceof Element ? event.target : null);
}

function trackClick(event: MouseEvent): void {
  const target = event.target;
  trackOutsideDialogs(target instanceof Element ? target.closest(FOCUSABLE_SELECTOR) : null);
}

function trackPointerDown(event: PointerEvent): void {
  lastPointerType = event.pointerType;
}

function trackKeyDown(): void {
  lastPointerType = null;
}

if (typeof document !== "undefined") {
  document.addEventListener("focusin", trackFocusIn, true);
  document.addEventListener("click", trackClick, true);
  document.addEventListener("pointerdown", trackPointerDown, true);
  document.addEventListener("keydown", trackKeyDown, true);
  import.meta.hot?.dispose(() => {
    document.removeEventListener("focusin", trackFocusIn, true);
    document.removeEventListener("click", trackClick, true);
    document.removeEventListener("pointerdown", trackPointerDown, true);
    document.removeEventListener("keydown", trackKeyDown, true);
    stopWatchingOpener?.();
  });
}

function restoreFocusTo(opener: HTMLElement): boolean {
  if (!opener.isConnected || opener.closest("[hidden], [inert]") !== null) return false;
  opener.focus();
  return document.activeElement === opener;
}

function focusIsLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body;
}

function afterQueuedTasks(run: () => void): void {
  const channel = new MessageChannel();
  channel.port1.onmessage = () => {
    channel.port1.close();
    run();
  };
  channel.port2.postMessage(null);
}

function focusTransiently(target: HTMLElement): boolean {
  if (target === document.querySelector("main")) return focusMainTransiently();
  if (target.hasAttribute("tabindex")) {
    target.focus();
    return document.activeElement === target;
  }
  target.tabIndex = -1;
  target.focus();
  const blurOnClick = () => {
    if (document.activeElement === target) target.blur();
  };
  const release = () => {
    target.removeEventListener("blur", release);
    target.removeEventListener("pointerdown", release, true);
    target.removeEventListener("click", blurOnClick, true);
    target.removeAttribute("tabindex");
  };
  if (document.activeElement !== target) {
    release();
    return false;
  }
  target.addEventListener("blur", release);
  target.addEventListener("pointerdown", release, true);
  target.addEventListener("click", blurOnClick, true);
  return true;
}

function isPrintableKey(event: KeyboardEvent): boolean {
  const shortcut = event.metaKey || (event.ctrlKey && !event.getModifierState("AltGraph"));
  return event.key.length === 1 && !shortcut;
}

function handTypingTo(dialog: HTMLDialogElement, field: HTMLElement): void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.target === dialog && isPrintableKey(event)) field.focus();
  };
  const stop = () => {
    dialog.removeEventListener("keydown", onKeyDown);
    dialog.removeEventListener("blur", stop);
  };
  dialog.addEventListener("keydown", onKeyDown);
  dialog.addEventListener("blur", stop);
}

function focusInitial(dialog: HTMLDialogElement, target: HTMLElement | undefined): void {
  const next = target ?? firstFocusable(dialog);
  if (next && lastPointerType === "touch" && raisesKeyboard(next) && focusTransiently(dialog)) {
    handTypingTo(dialog, next);
    return;
  }
  next?.focus();
}

function nearestShownAncestor(chain: HTMLElement[]): HTMLElement | undefined {
  return chain.find((node) => node.isConnected && node.closest("[hidden], [inert]") === null);
}

let stopWatchingOpener: (() => void) | null = null;

function watchOpenerWhileFocused(opener: HTMLElement): void {
  stopWatchingOpener?.();
  const app = appElement();
  if (!app) return;
  const ancestors: HTMLElement[] = [];
  for (let node = opener.parentElement; node && node !== app; node = node.parentElement) {
    ancestors.push(node);
  }
  const observer = new MutationObserver(() => {
    if (opener.isConnected) return;
    stop();
    afterQueuedTasks(() => {
      if (!focusIsLost()) return;
      const survivor = nearestShownAncestor(ancestors);
      if (!survivor || !focusTransiently(survivor)) focusMainTransiently();
    });
  });
  const onFocusOut = () => {
    queueMicrotask(() => {
      if (opener.isConnected && document.activeElement !== opener) stop();
    });
  };
  const onFocusIn = (event: FocusEvent) => {
    if (event.target !== opener) stop();
  };
  const stop = () => {
    observer.disconnect();
    opener.removeEventListener("focusout", onFocusOut);
    document.removeEventListener("focusin", onFocusIn, true);
    if (stopWatchingOpener === stop) stopWatchingOpener = null;
  };
  observer.observe(app, { childList: true, subtree: true });
  opener.addEventListener("focusout", onFocusOut);
  document.addEventListener("focusin", onFocusIn, true);
  stopWatchingOpener = stop;
}

const stackListeners = new Set<() => void>();

function notifyStack(): void {
  for (const listener of stackListeners) listener();
}

function subscribeStack(listener: () => void): () => void {
  stackListeners.add(listener);
  return () => {
    stackListeners.delete(listener);
  };
}

function topDialog(): string | undefined {
  return openDialogs[openDialogs.length - 1];
}

const noTopDialog = () => undefined;

function anyDialogOpen(): boolean {
  return openDialogs.length > 0;
}

const noDialogOpen = () => false;

export function useAnyDialogOpen(): boolean {
  return useSyncExternalStore(subscribeStack, anyDialogOpen, noDialogOpen);
}

function applyAppInert(): void {
  const app = appElement();
  if (!app) return;
  if (openDialogs.length > 0) app.setAttribute("inert", "");
  else app.removeAttribute("inert");
}

function applyStackInert(): void {
  const top = topDialog();
  for (const [id, el] of dialogNodes) {
    if (openDialogs.includes(id) && id !== top) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }
}

interface StyleSnapshot {
  overflow: string;
  paddingRight: string;
  overscrollBehavior: string;
}

interface LockedNode {
  el: HTMLElement;
  snap: StyleSnapshot;
}

let lockedNodes: LockedNode[] = [];
let lockListening = false;
const touchStartY = new Map<number, number>();

function isVerticallyScrollable(el: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(el);
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") {
    return false;
  }
  return el.scrollHeight > el.clientHeight;
}

function canScrollVertically(el: HTMLElement, deltaY: number): boolean {
  if (deltaY < 0) return el.scrollTop > 0;
  return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
}

function scrollableInside(target: EventTarget | null, boundary: HTMLElement): HTMLElement | null {
  let node: Element | null =
    target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  while (node && boundary.contains(node)) {
    if (node instanceof HTMLElement && isVerticallyScrollable(node)) return node;
    if (node === boundary) break;
    node = node.parentElement;
  }
  if (isVerticallyScrollable(boundary)) return boundary;
  return null;
}

const SCROLL_CONTAINER_SELECTOR = [
  "overflow-auto",
  "overflow-scroll",
  "overflow-y-auto",
  "overflow-y-scroll",
]
  .map((utility) => `[class*="${utility}"]`)
  .join(",");

function collectLockTargets(): HTMLElement[] {
  const targets: HTMLElement[] = [document.documentElement, document.body];
  const app = appElement();
  if (app) {
    targets.push(app);
    if (typeof app.querySelectorAll === "function") {
      for (const node of app.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR)) {
        if (isVerticallyScrollable(node)) targets.push(node);
      }
    }
  }
  const main = document.querySelector("main");
  if (main instanceof HTMLElement) targets.push(main);
  return [...new Set(targets)];
}

function elementScrollbarGap(el: HTMLElement): number {
  if (el === document.documentElement) return 0;
  if (el === document.body) {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }
  return Math.max(0, el.offsetWidth - el.clientWidth);
}

function resolveEventTarget(
  clientX: number,
  clientY: number,
  fallback: EventTarget | null,
): Node | null {
  const fromPoint =
    typeof document.elementFromPoint === "function"
      ? document.elementFromPoint(clientX, clientY)
      : null;
  if (fromPoint) return fromPoint;
  if (fallback instanceof Node) return fallback;
  return null;
}

function onLockWheel(event: WheelEvent): void {
  if (openDialogs.length === 0) return;
  const top = topStackedDialog();
  if (!top) return;
  const target = resolveEventTarget(event.clientX, event.clientY, event.target);
  if (target && top.contains(target)) {
    const scroller = scrollableInside(target, top);
    if (scroller && canScrollVertically(scroller, event.deltaY)) return;
  }
  event.preventDefault();
}

function onLockTouchStart(event: TouchEvent): void {
  if (openDialogs.length === 0) return;
  for (const touch of event.changedTouches) {
    touchStartY.set(touch.identifier, touch.clientY);
  }
}

function onLockTouchEnd(event: TouchEvent): void {
  for (const touch of event.changedTouches) {
    touchStartY.delete(touch.identifier);
  }
}

function onLockTouchMove(event: TouchEvent): void {
  if (openDialogs.length === 0) return;
  const top = topStackedDialog();
  if (!top) return;
  let touch: Touch | undefined;
  for (const candidate of event.touches) {
    if (touchStartY.has(candidate.identifier)) {
      touch = candidate;
      break;
    }
  }
  touch ??= event.touches[0];
  if (!touch) return;
  const target = resolveEventTarget(touch.clientX, touch.clientY, event.target);
  if (target && top.contains(target)) {
    const scroller = scrollableInside(target, top);
    const startY = touchStartY.get(touch.identifier);
    const deltaY = startY == null ? 0 : startY - touch.clientY;
    if (scroller && canScrollVertically(scroller, deltaY)) return;
  }
  event.preventDefault();
}

function onLockPageShow(): void {
  if (openDialogs.length === 0 && (lockedNodes.length > 0 || lockListening)) {
    releaseScrollLock();
  }
}

function engageScrollLock(): void {
  if (lockedNodes.length > 0) return;
  const planned = collectLockTargets().map((el) => ({
    el,
    gap: elementScrollbarGap(el),
    snap: {
      overflow: el.style.overflow,
      paddingRight: el.style.paddingRight,
      overscrollBehavior: el.style.overscrollBehavior,
    },
  }));
  for (const { el, gap, snap } of planned) {
    lockedNodes.push({ el, snap });
    el.style.overflow = "hidden";
    el.style.overscrollBehavior = "none";
    if (gap > 0) {
      const prior = Number.parseFloat(getComputedStyle(el).paddingRight) || 0;
      el.style.paddingRight = `${prior + gap}px`;
    }
  }
  if (!lockListening) {
    document.addEventListener("wheel", onLockWheel, { capture: true, passive: false });
    document.addEventListener("touchstart", onLockTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onLockTouchMove, { capture: true, passive: false });
    document.addEventListener("touchend", onLockTouchEnd, { capture: true, passive: true });
    document.addEventListener("touchcancel", onLockTouchEnd, { capture: true, passive: true });
    window.addEventListener("pageshow", onLockPageShow);
    document.addEventListener("visibilitychange", onLockPageShow);
    lockListening = true;
  }
}

function releaseScrollLock(): void {
  for (const { el, snap } of lockedNodes) {
    el.style.overflow = snap.overflow;
    el.style.paddingRight = snap.paddingRight;
    el.style.overscrollBehavior = snap.overscrollBehavior;
  }
  lockedNodes = [];
  touchStartY.clear();
  if (lockListening) {
    document.removeEventListener("wheel", onLockWheel, { capture: true });
    document.removeEventListener("touchstart", onLockTouchStart, { capture: true });
    document.removeEventListener("touchmove", onLockTouchMove, { capture: true });
    document.removeEventListener("touchend", onLockTouchEnd, { capture: true });
    document.removeEventListener("touchcancel", onLockTouchEnd, { capture: true });
    window.removeEventListener("pageshow", onLockPageShow);
    document.removeEventListener("visibilitychange", onLockPageShow);
    lockListening = false;
  }
}

function syncScrollLock(): void {
  if (openDialogs.length > 0) engageScrollLock();
  else releaseScrollLock();
}

function syncStack(): void {
  applyAppInert();
  syncInertWindow(appElement(), openDialogs.length > 0);
  applyStackInert();
  syncScrollLock();
  notifyStack();
}

function pushDialog(id: string): void {
  if (openDialogs.includes(id)) return;
  openDialogs.push(id);
  syncStack();
}

function dropDialog(id: string): void {
  const index = openDialogs.indexOf(id);
  if (index < 0) return;
  openDialogs.splice(index, 1);
  syncStack();
}

function topStackedDialog(): HTMLDialogElement | undefined {
  const top = topDialog();
  return top ? dialogNodes.get(top) : undefined;
}

function coveredDialog(except: HTMLDialogElement): HTMLDialogElement | undefined {
  for (let index = openDialogs.length - 1; index >= 0; index -= 1) {
    const id = openDialogs[index];
    const node = id ? dialogNodes.get(id) : undefined;
    if (node && node !== except) return node;
  }
  return undefined;
}

interface CoveredDialog {
  dialog: HTMLDialogElement | undefined;
  mark: HTMLElement | undefined;
}

function handFocusToOpenDialog(covered: CoveredDialog) {
  const top = topStackedDialog();
  if (!top) return;
  const marked = markedTarget(top);
  if (top === covered.dialog && marked && marked !== covered.mark) {
    focusInitial(top, marked);
    return;
  }
  if (top.contains(document.activeElement)) return;
  focusInitial(top, initialFocusTarget(top));
}

type DialogSize = "md" | "lg";

const SIZE_CLASS: Record<DialogSize, string> = {
  md: "max-w-md",
  lg: "max-w-2xl",
};

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  describedBy?: string;
  error?: string;
  size?: DialogSize;
  children: ReactNode;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  describedBy,
  error,
  size = "md",
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const ceremonyActive = useSyncExternalStore(
    subscribeCeremonyActivity,
    isCeremonyActive,
    ceremonyInactive,
  );
  const dialogId = useId();
  const top = useSyncExternalStore(subscribeStack, topDialog, noTopDialog);
  const isTop = open && (top === dialogId || !openDialogs.includes(dialogId));
  const [rearm, setRearm] = useState(0);
  const openRef = useRef(open);
  const openerRef = useRef<HTMLElement | null>(null);
  const coveredRef = useRef<CoveredDialog>({ dialog: undefined, mark: undefined });
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const errorId = useId();

  useLayoutEffect(() => {
    openRef.current = open;
  }, [open]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    dialogNodes.set(dialogId, el);
    pushDialog(dialogId);
    return () => {
      dropDialog(dialogId);
      dialogNodes.delete(dialogId);
    };
  }, [open, dialogId]);

  useEffect(() => {
    const el = ref.current;
    return () => {
      openRef.current = false;
      if (el?.open) el.close();
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !el.isConnected) return;

    if (!open) {
      if (el.open) {
        const strandedFocus = el.contains(document.activeElement);
        el.close();
        const opener = openerRef.current;
        openerRef.current = null;
        handFocusToOpenDialog(coveredRef.current);
        const active = document.activeElement;
        const lostOnClose = focusIsLost() || (strandedFocus && el.contains(active));
        if (openDialogs.length === 0) {
          if (opener && (active === opener || (lostOnClose && restoreFocusTo(opener)))) {
            watchOpenerWhileFocused(opener);
          } else if (opener) {
            if (lostOnClose) focusMainTransiently();
          } else if (lostOnClose) {
            afterQueuedTasks(() => {
              if (!el.open && (focusIsLost() || el.contains(document.activeElement))) {
                focusMainTransiently();
              }
            });
          }
        }
      }
      return;
    }

    if (el.open) return;
    const active = document.activeElement;
    openerRef.current = isAppControlOutsideDialogs(active)
      ? active
      : lastControlOutsideDialogs?.isConnected
        ? lastControlOutsideDialogs
        : null;
    const covered = coveredDialog(el);
    coveredRef.current = { dialog: covered, mark: covered ? markedTarget(covered) : undefined };
    try {
      el.show();
    } catch {
      dropDialog(dialogId);
      dialogNodes.delete(dialogId);
      onClose();
      return;
    }
    focusInitial(el, initialFocusTarget(el));
  }, [open, dialogId, onClose]);

  useEffect(() => {
    if (!isTop) return;
    const watcher = newCloseWatcher();
    if (watcher) {
      watcher.addEventListener("close", () => {
        onClose();
        setRearm((count) => count + 1);
      });
      return () => watcher.destroy();
    }
    const onDocumentKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      onClose();
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [isTop, onClose, rearm]);

  const handleNativeClose = () => {
    const el = ref.current;
    if (openRef.current && el && el.isConnected && !el.open) {
      queueMicrotask(() => {
        if (openRef.current && el.isConnected && !el.open) {
          try {
            el.show();
          } catch {
            dropDialog(dialogId);
            dialogNodes.delete(dialogId);
            onClose();
            return;
          }
        }
      });
    }
  };

  const ariaDescribedBy = [
    description ? descriptionId : null,
    error ? errorId : null,
    describedBy ?? null,
  ]
    .filter(Boolean)
    .join(" ");

  const scrimTarget = open && isTop ? scrimHost() : null;
  const dialogTarget = dialogHost();

  const dialog = (
    <dialog
      ref={ref}
      onClose={handleNativeClose}
      aria-labelledby={titleId}
      aria-describedby={ariaDescribedBy || undefined}
      aria-modal={isTop && !ceremonyActive ? "true" : undefined}
      className={`
        fixed inset-0 m-auto w-full ${SIZE_CLASS[size]} max-h-[calc(100%-2em)] overflow-auto
        rounded-2xl border border-border bg-surface p-0 shadow-2xl shadow-black/20 outline-none
        open:animate-in open:fade-in-0 open:zoom-in-95
        ${open && !isTop ? "opacity-40" : ""}
      `}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-heading">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm text-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <p id={errorId} role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="mt-5">{children}</div>
      </div>
    </dialog>
  );

  return (
    <>
      {scrimTarget &&
        createPortal(
          <div
            aria-hidden="true"
            onClick={ceremonyActive ? undefined : onClose}
            className={`fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm ${
              ceremonyActive ? "pointer-events-none" : ""
            }`}
          />,
          scrimTarget,
        )}
      {createPortal(dialog, dialogTarget ?? document.body)}
    </>
  );
}
